import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import type { RadioGroupProps, RadioGroupItemProps } from './types';

const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

/**
 * Radix checks the radio an arrow key moves to only while the key is still
 * down as focus lands. A quick press, or a screen reader passing the key on,
 * has released it by then, so focus moved without checking.
 */
function checkFocusedRadio(group: HTMLElement) {
  // Queued after the timeout in which Radix moves focus.
  setTimeout(() => {
    const radio = document.activeElement;
    if (
      radio instanceof HTMLElement &&
      radio.getAttribute('role') === 'radio' &&
      group.contains(radio) &&
      radio.dataset.state !== 'checked'
    )
      radio.click();
  });
}

const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, children, onKeyDown, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn('eye:grid eye:gap-4xs', className)}
    onKeyDown={(event) => {
      onKeyDown?.(event);
      if (ARROW_KEYS.includes(event.key))
        checkFocusedRadio(event.currentTarget);
    }}
    {...props}
  >
    {children}
  </RadioGroupPrimitive.Root>
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, label, id, value, ...props }, ref) => {
  const generatedId = React.useId();
  const itemId = id ?? generatedId;
  const labelId = `${itemId}-label`;

  return (
    <div
      className={cn(
        'eye:flex eye:w-max eye:cursor-pointer eye:items-center eye:gap-3xs',
        'eye:has-[button:disabled]:cursor-not-allowed eye:has-[button:disabled]:**:data-[slot=label]:cursor-not-allowed',
      )}
    >
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          'eye:focus-visible:ring-ring eye:flex eye:aspect-square eye:h-20 eye:w-20 eye:shrink-0 eye:cursor-pointer eye:items-center eye:justify-center eye:rounded-full eye:border eye:border-black eye:text-black eye:focus:outline-none eye:focus-visible:ring-1 eye:disabled:cursor-not-allowed eye:disabled:opacity-50 eye:data-[state=checked]:border-blue-100 eye:data-[state=checked]:text-blue-100',
          className,
        )}
        id={itemId}
        value={value}
        aria-labelledby={label ? labelId : undefined}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className='eye:flex eye:size-12 eye:rounded-full eye:bg-current' />
      </RadioGroupPrimitive.Item>
      {label && (
        <Label id={labelId} className='eye:cursor-pointer' htmlFor={itemId}>
          {label}
        </Label>
      )}
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
